import * as React from 'react';
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageLayout, AdminSectionCard } from '../../components/admin/AdminPageLayout';
import { AdminAction, AdminFormFooter } from '../../components/admin';
import { createAdminUser, updateAdminUser, getAdminUser } from '.';

const ROLE_OPTIONS = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Editor', label: 'Editor' },
];

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const initialForm = {
  fullName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'Editor',
  status: 'Active',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fafaf9',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a8a29e' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#78716c', borderWidth: '2px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#57534e' },
};

export default function AdminUserFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [fetching, setFetching] = React.useState(isEdit);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  // Load existing user data in edit mode
  React.useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    getAdminUser(id)
      .then((res) => {
        const u = res.data.data;
        setForm({
          fullName: u.fullName || '',
          username: u.username || '',
          email: u.email || '',
          password: '',
          confirmPassword: '',
          role: u.role || 'Editor',
          status: u.status || 'Active',
        });
      })
      .catch(() => {
        toast.error('Failed to load user details.');
        navigate('/admin-users');
      })
      .finally(() => setFetching(false));
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};

    if (!form.fullName.trim()) errs.fullName = 'Full name is required';

    if (!form.username.trim()) {
      errs.username = 'Username is required';
    } else if (!/^[a-z0-9_]{3,30}$/.test(form.username.trim().toLowerCase())) {
      errs.username = 'Username must be 3–30 characters (letters, numbers, underscores only)';
    }

    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = 'Enter a valid email address';
    }

    if (!form.role) errs.role = 'Role is required';

    // Password required only on create; optional on edit (only validate if filled)
    if (!isEdit) {
      if (!form.password) errs.password = 'Password is required';
      else if (form.password.length < 8) errs.password = 'Minimum 8 characters';

      if (!form.confirmPassword) {
        errs.confirmPassword = 'Please confirm your password';
      } else if (form.password !== form.confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
    } else if (form.password) {
      if (form.password.length < 8) errs.password = 'Minimum 8 characters';
      if (!form.confirmPassword) {
        errs.confirmPassword = 'Please confirm the new password';
      } else if (form.password !== form.confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        username: form.username.trim().toLowerCase(),
        email: form.email.trim(),
        role: form.role,
        status: form.status,
      };

      // Only include password fields if provided
      if (form.password) {
        payload.password = form.password;
        payload.confirmPassword = form.confirmPassword;
      }

      if (isEdit) {
        await updateAdminUser(id, payload);
        toast.success('User updated successfully!');
      } else {
        await createAdminUser(payload);
        toast.success('User created successfully!');
      }
      navigate('/admin-users');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <AdminPageLayout eyebrow="Users / Admin Users" title={isEdit ? 'Edit User' : 'Add User'}>
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <CircularProgress size={24} sx={{ color: '#78716c' }} />
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      eyebrow="Users / Admin Users"
      title={isEdit ? 'Edit Admin User' : 'Add Admin User'}
      description={
        isEdit
          ? 'Update user details. Leave password fields blank to keep the existing password.'
          : 'Fill in the details below to create a new admin panel user.'
      }
      actions={
        <AdminAction
          id="admin-user-form-back-btn"
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate('/admin-users')}
        >
          Back to Users
        </AdminAction>
      }
    >
      <AdminSectionCard
        title="User information"
        description={
          isEdit
            ? 'Update the fields below. Password is optional — only fill in to reset.'
            : 'Fields marked with * are required.'
        }
      >
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5} sx={{ p: 2 }}>

            {/* Full Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-fullname"
                fullWidth
                required
                label="Full Name *"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                error={!!errors.fullName}
                helperText={errors.fullName}
                size="small"
                sx={inputSx}
                placeholder="e.g. Rahul Sharma"
              />
            </Grid>

            {/* Username */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-username"
                fullWidth
                required
                label="Username *"
                name="username"
                value={form.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username || 'Lowercase letters, numbers, underscores (3–30 chars)'}
                size="small"
                sx={inputSx}
                placeholder="e.g. name_admin"
                inputProps={{ maxLength: 30 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-email"
                fullWidth
                required
                label="Email Address *"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
                sx={inputSx}
                placeholder="admin@example.com"
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-role"
                select
                fullWidth
                required
                label="Role *"
                name="role"
                value={form.role}
                onChange={handleChange}
                error={!!errors.role}
                helperText={errors.role}
                size="small"
                sx={inputSx}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-status"
                select
                fullWidth
                label="Status"
                name="status"
                value={form.status}
                onChange={handleChange}
                size="small"
                sx={inputSx}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-password"
                fullWidth
                required={!isEdit}
                label={isEdit ? 'New Password (optional)' : 'Password *'}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || (isEdit ? 'Leave blank to keep current password' : 'Minimum 8 characters')}
                size="small"
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="admin-user-confirm-password"
                fullWidth
                required={!isEdit || !!form.password}
                label={isEdit ? 'Confirm New Password' : 'Confirm Password *'}
                name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                size="small"
                sx={inputSx}
                disabled={isEdit && !form.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowConfirm((s) => !s)} edge="end" disabled={isEdit && !form.password}>
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <AdminFormFooter className="border-t border-stone-200 mt-1">
                <AdminAction
                  id="admin-user-form-cancel-btn"
                  variant="outline"
                  onClick={() => navigate('/admin-users')}
                  disabled={loading}
                >
                  Cancel
                </AdminAction>
                <AdminAction
                  id="admin-user-form-submit-btn"
                  type="submit"
                  loading={loading}
                >
                  {isEdit ? 'Save Changes' : 'Create User'}
                </AdminAction>
              </AdminFormFooter>
            </Grid>
          </Grid>
        </form>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
